import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator, MatPaginatorIntl } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatSelectModule } from '@angular/material/select';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FormGroup, FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { AlertDialogComponent } from '../../shared/alert-dialog/alert-dialog.component';
import { SmsDetailsDialogComponent } from '../../shared/sms-details-dialog/sms-details-dialog.component';
import * as XLSX from 'xlsx';
import { provideNativeDateAdapter } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { ApplicationDataService } from '../../core/services/application-data.service';
import { getCustomPaginatorIntl } from '../../shared/paginator.helper';
import { Config } from '../../core/DefaultConfig';

interface SmsTrackingData {
  smsTrackingId: number;
  createdAt: string;
  smsType: string;
  sendTo: string;
  sendby: string;
  description: string;
  status: number;
  response: string;
}

@Component({
  selector: 'app-sms-tracking',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatSelectModule,
    MatMenuModule,
    MatDialogModule,
    MatTooltipModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
  ],
  templateUrl: './sms-tracking.component.html',
  styleUrls: ['./sms-tracking.component.css'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    provideNativeDateAdapter(),
    { provide: MatPaginatorIntl, useValue: getCustomPaginatorIntl('Sms-tracking') }
  ],
})
export class SmsTrackingComponent implements OnInit {
  form: FormGroup;
  displayedColumns: string[] = ['check-box', 'createdAt', 'smsType', 'sendTo', 'sendby', 'description', 'status', 'response', 'action'];
  dataSource: MatTableDataSource<SmsTrackingData>;
  selection = new SelectionModel<SmsTrackingData>(true, []);
  config = new Config();

  totalSmsCount: number = 0;
  sendSmsCount: number = 0;
  failedSmsCount: number = 0;
  totalRecords: number = 0;

  // Details API pagination
  selectedStatusFilter: string = '';
  pageNo: number = 0;
  pageSize: number = 10;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private formBuilder: FormBuilder,
    private appDataService: ApplicationDataService,
    private dialog: MatDialog
  ) {
    this.dataSource = new MatTableDataSource();
  }

  ngOnInit(): void {
    this.initForm();
    this.loadSmsCount();
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  initForm(): void {
    this.form = this.formBuilder.group({
      dateFrom: [null],
      dateTo: [null],
      smsType: [''],
      sendTo: [''],
      sendby: [''],
    });

    this.form.get('dateFrom')?.valueChanges.subscribe(() => this.onDateChange());
    this.form.get('dateTo')?.valueChanges.subscribe(() => this.onDateChange());
    this.form.get('smsType')?.valueChanges.subscribe(() => this.onSmsTypeChange());
  }

  onDateChange(): void {
    const dateFrom = this.form.get('dateFrom')?.value;
    const dateTo = this.form.get('dateTo')?.value;

    // Only call API when both dates are selected
    if (dateFrom && dateTo) {
      // Reset card selection and clear table
      this.selectedStatusFilter = '';
      this.dataSource = new MatTableDataSource();
      this.totalRecords = 0;
      this.pageNo = 0;
      this.loadSmsCount();
    }
  }

  onSmsTypeChange(): void {
    // Reset card selection and clear table
    this.selectedStatusFilter = '';
    this.dataSource = new MatTableDataSource();
    this.totalRecords = 0;
    this.pageNo = 0;
    this.loadSmsCount();
  }

  loadSmsCount(): void {
    const dateFrom = this.form.get('dateFrom')?.value;
    const dateTo = this.form.get('dateTo')?.value;
    const smsType = this.form.get('smsType')?.value;

    const payload = {
      dateFrom: dateFrom ? `${this.formatDateValue(dateFrom)}T00:00:00` : '',
      dateTo: dateTo ? `${this.formatDateValue(dateTo)}T23:59:59` : '',
      smsType: smsType || ''
    };

    this.appDataService.getSmsTrackingCount(payload).subscribe({
      next: (res: any) => {
        if (res?.data) {
          this.totalSmsCount = res.data.totalSms ?? 0;
          this.sendSmsCount = res.data.successfulSms ?? 0;
          this.failedSmsCount = res.data.failedSms ?? 0;
        }
      }
    });
  }

  setDateRange(days: number): void {
    const today = new Date();
    const fromDate = new Date(today.getTime() - days * 24 * 60 * 60 * 1000);

    this.form.patchValue({
      dateFrom: this.formatDate(fromDate),
      dateTo: this.formatDate(today)
    }, { emitEvent: false });

    // Reset and load count once
    this.selectedStatusFilter = '';
    this.dataSource = new MatTableDataSource();
    this.totalRecords = 0;
    this.pageNo = 0;
    this.loadSmsCount();
  }

  setLastMonth(): void {
    const today = new Date();
    const fromDate = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

    this.form.patchValue({
      dateFrom: this.formatDate(fromDate),
      dateTo: this.formatDate(today)
    }, { emitEvent: false });

    // Reset and load count once
    this.selectedStatusFilter = '';
    this.dataSource = new MatTableDataSource();
    this.totalRecords = 0;
    this.pageNo = 0;
    this.loadSmsCount();
  }


  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatDateValue(value: Date | string): string {
    if (value instanceof Date) {
      return this.formatDate(value);
    }
    return value;
  }

  onCardClick(statusFilter: string): void {
    this.selectedStatusFilter = statusFilter;
    this.pageNo = 0;
    this.loadSmsDetails();
  }

  onSearch(): void {
    if (!this.selectedStatusFilter) {
      this.openAlert('Please select a status filter (Total, Send, or Failed) first.');
      return;
    }
    this.pageNo = 0;
    this.loadSmsDetails();
  }

  loadSmsDetails(): void {
    if (!this.selectedStatusFilter) return;
    this.selection.clear();

    const dateFrom = this.form.get('dateFrom')?.value;
    const dateTo = this.form.get('dateTo')?.value;
    const smsType = this.form.get('smsType')?.value;
    const sendTo = this.form.get('sendTo')?.value;
    const sendby = this.form.get('sendby')?.value;

    const payload = {
      dateFrom: dateFrom ? `${this.formatDateValue(dateFrom)}T00:00:00` : '',
      dateTo: dateTo ? `${this.formatDateValue(dateTo)}T23:59:59` : '',
      smsType: smsType || '',
      sendTo: sendTo || '',
      sendby: sendby || '',
      statusFilter: this.selectedStatusFilter,
      pageNo: this.pageNo,
      pageSize: this.pageSize
    };

    this.appDataService.getSmsTrackingDetails(payload).subscribe({
      next: (res: any) => {
        console.log('SMS Details Response:', res);
        if (res?.data?.data) {
          const content = res.data.data;
          this.dataSource = new MatTableDataSource(content);
          // Check for totalRows or totalElements
          this.totalRecords = res.data.totalRows ?? res.data.totalElements ?? content.length;
          this.dataSource.sort = this.sort;
        }
      }
    });
  }

  onPageChange(event: any): void {
    this.pageNo = event.pageIndex;
    this.pageSize = event.pageSize;
    this.selection.clear();
    this.loadSmsDetails();
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  exportCurrentAsExcel(): void {
    // Remove focus from button
    (document.activeElement as HTMLElement)?.blur();

    if (this.selection.selected.length === 0) {
      this.openAlert('Please select the rows you would like to export.');
      return;
    }
    this.exportToExcel(this.selection.selected, 'sms-tracking-selected.xlsx');
  }

  exportAllAsExcel(): void {
    if (!this.dataSource.data || this.dataSource.data.length === 0) {
      this.openAlert('There is nothing to export.');
      return;
    }

    const dateFrom = this.form.get('dateFrom')?.value;
    const dateTo = this.form.get('dateTo')?.value;
    const smsType = this.form.get('smsType')?.value;
    const sendTo = this.form.get('sendTo')?.value;
    const sendby = this.form.get('sendby')?.value;

    const payload = {
      dateFrom: dateFrom ? `${this.formatDateValue(dateFrom)}T00:00:00` : '',
      dateTo: dateTo ? `${this.formatDateValue(dateTo)}T23:59:59` : '',
      smsType: smsType || '',
      sendTo: sendTo || '',
      sendby: sendby || '',
      statusFilter: this.selectedStatusFilter,
      pageNo: 0,
      pageSize: this.totalRecords || 10000
    };

    this.appDataService.getSmsTrackingDetails(payload).subscribe({
      next: (res: any) => {
        if (res?.data?.data) {
          this.exportToExcel(res.data.data, 'sms-tracking-all.xlsx');
        }
      }
    });
  }

  private formatDateTime(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  }

  private exportToExcel(data: SmsTrackingData[], fileName: string): void {
    const exportData = data.map((item) => ({
      'SENT DATE/TIME': this.formatDateTime(item.createdAt),
      'SMS TYPE': item.smsType || "N/A",
      'RECIPIENT': item.sendTo || "N/A",
      'SENDER': item.sendby || "N/A",
      'MESSAGE CONTENT': item.description || "N/A",
      'STATUS': item.status === 200 ? 'Sent' : 'Failed',
      'REASON': item.response || "N/A"
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'SMS Tracking');

    // Auto-adjust column widths
    const colWidths = [
      { wch: 20 },  // Sent Date/Time
      { wch: 12 },  // SMS Type
      { wch: 15 },  // Recipient
      { wch: 15 },  // Send By
      { wch: 40 },  // Message Content
      { wch: 10 },  // Status
      { wch: 30 }   // Response
    ];
    worksheet['!cols'] = colWidths;

    XLSX.writeFile(workbook, fileName);
  }

  openSmsDetails(row: SmsTrackingData): void {
    this.dialog.open(SmsDetailsDialogComponent, {
      data: row,
      width: '900px',
      height: '600px',
      maxWidth: '95vw',
      disableClose: true,
      autoFocus: false,
      panelClass: 'sms-details-dialog-panel'
    });
  }

  openAlert(message: string) {
    this.dialog.open(AlertDialogComponent, {
      data: {
        message: message,
        buttonText: {
          cancel: 'OK'
        }
      },
    });
  }
}
